<template>
  <div
    class="relative bg-semantic-inverse-surface after:content-[''] after:absolute after:w-full after:h-full after:top-0 after:left-0 after:right-0 after:bottom-0 after:bg-purple-90/80 after:rounded-2.5xl after:pointer-events-none rounded-2.5xl px-4 py-6 lg:px-0 lg:py-12 md:after:rounded-none md:rounded-none"
  >
    <div class="w-full max-w-wrapper-xs2 mx-auto relative z-[1] flex flex-col gap-2 md:flex-row md:items-center md:gap-9">
      <!-- eslint-disable vue/no-v-html -->
      <div
        class="flex-1 text-2xl/[33px] md:text-4.5xl text-white"
        v-html="VERTICAL_DATA?.title"
      >
      </div>

      <div class="flex-1">
        <form
          class="flex flex-col gap-3"
          @submit.prevent="handleSubmit"
        >
          <div
            v-for="(formItem, formItemKey) in VERTICAL_DATA?.formItems"
            :key="formItemKey"
            :class="formItem?.componentClass"
          >
            <component
              :is="getKey(formItem.component, componentMap)"
              v-bind="formItem.props"
              v-model="formModel[formItemKey]"
              class="lg:text-base"
              v-on="{
                update: () => { 
                  handleValidateForm(formItemKey)
                  handleValueUpdate(formItemKey)
                }
              }"    
            />
          </div>

          <div v-if="VERTICAL_DATA?.formItems">
            <atoms-button
              type="submit"
              size="medium"
              class="w-full justify-center border !border-semantic-inverse-primary hover:!border-semantic-inverse-primary-variant !bg-semantic-inverse-primary hover:!bg-semantic-inverse-primary-variant !text-semantic-inverse-on-primary h-10"
            >
              Continue
            </atoms-button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import _isEmpty from 'underscore/cjs/isEmpty.js'
import _omit from 'underscore/cjs/omit.js'

import {
  HERO_WIDGET_DATA_OBJ,
  HERO_ERROR_MAP_OBJ
} from '~/constants/funnel/vertical-widget'

import {
  PAGE_DATA
} from '~/constants/funnel/ovhc'

import { validateAge } from '~/helpers/validator'

import {
  GENERATE_FORM_MODEL_TEST
} from '~/helpers/formfunnel.helpers'

const $route = useRoute()
const $router = useRouter()

const healthInsuranceStore = useHealthInsuranceStore()
const ovhcStore = useOvhcStore()
const lifeInsuranceStore = useLifeInsuranceStore()
const incomeProtectionStore = useIncomeProtectionStore()
const homeLoansStore = useHomeLoansStore()
const energyStore = useEnergyStore()
const carInsuranceStore = useCarInsuranceStore()

defineOptions({
  name: 'MoleculesVerticalFormWidgetHero'
})

const props = defineProps({
  vertical: {
    type: String,
    default: ''
  },

  isIncomeProtection: {
    type: Boolean,
    default: false
  },

  isOvhc: {
    type: Boolean,
    default: false
  }
})

const _vertical = computed(() => {
  if (
    props.vertical === 'Life Insurance' &&
    props.isIncomeProtection
  ) {
    return 'Income Protection'
  }

  if (
    props.vertical === 'Health Insurance' &&
    props.isOvhc
  ) { 
    return 'OVHC'
  }

  return props.vertical
})

const ERROR_MAP = getKey(_vertical.value, HERO_ERROR_MAP_OBJ)

const formModel = ref({})
const validation = ref({})

const componentMap = {
  MoleculesSelection: resolveComponent('MoleculesSelection'),
  MoleculesSelectDropdown: resolveComponent('MoleculesSelectDropdown'),
  AtomsInput: resolveComponent('AtomsInput'),
  MoleculesPostcode: resolveComponent('MoleculesPostcode'),
  MoleculesOccupation: resolveComponent('MoleculesOccupation'),
  MoleculesStates: resolveComponent('MoleculesStates')
}

const VERTICAL_DATA = computed(() => {
  const form = JSON.parse(JSON.stringify(getKey(_vertical.value, HERO_WIDGET_DATA_OBJ) ?? {}))

  if (_isEmpty(form.formItems)) {
    return
  }

  form.formItems = (() => {
    const items = form.formItems
    
    if (_vertical.value === 'OVHC') {
      if (items?.visa_subtype) {
        form.formItems.visa_subtype.props.dataArr = formModel.value.visa_type
          ? [...getKey(formModel.value.visa_type.toLowerCase(), PAGE_DATA.visaType)]
          : []
      }

      if (formModel.value.visa_type !== 'Student') {
        form.formItems.visa_subtype.props.dataArr.push({
          name: '<strong><u>Other visa subclass</u></strong>',
          label: 'Other visa subclass',
          value: 'Other visa subclass'
        })
      }

      if (formModel.value.visa_subtype !== 'Other visa subclass') {
        return _omit(items, (_, key) => key === 'visa_code')
      }

      return items
    }

    return items
  })()

  for (const key in form.formItems) {
    if (Object.hasOwnProperty.call(form.formItems, key)) {
      const item = form.formItems[key]

      item.props.errorMessage = validation.value[key]?.errorMessage || ''
      item.props.isSuccess = validation.value[key]?.errorMessage || false
    }
  }

  return form
})

const _storeModel = computed(() => {
  return getKey(_vertical.value, {
    default: () => ({}),

    [VERTICAL.HEALTH_INSURANCE]: () => flattenObj(healthInsuranceStore.form, 1),

    OVHC: () => {
      const _temp = flattenObj(ovhcStore.form)

      return {
        life_stage: _temp?.life_stage,
        visa_subtype: _temp?.visa_subtype,
        visa_type: _temp?.visa_type,
        motivation: _temp?.motivation,
        visa_code: _temp?.visa_code
      }
    },

    [VERTICAL.LIFE_INSURANCE]: () => {
      const _temp = flattenObj(lifeInsuranceStore.form)

      return {
        age: _temp?.age,
        cover_amount: _temp?.annual_income
      }
    },

    'Income Protection': () => flattenObj(incomeProtectionStore.form),

    [VERTICAL.HOME_LOANS]: () => flattenObj(homeLoansStore.form),

    [VERTICAL.ENERGY]: () => flattenObj(energyStore.form, 1),

    [VERTICAL.CAR_INSURANCE]: () => flattenObj(carInsuranceStore.form, 1)
  })?.()
})

await handleModels()

function handleModels () {
  if (_isEmpty(VERTICAL_DATA.value)) {
    return {}
  }

  const form = Object.keys(VERTICAL_DATA.value?.formItems)

  formModel.value = _isEmpty(_storeModel.value)
    ? GENERATE_FORM_MODEL_TEST({ form })
    : _storeModel.value

  validation.value = GENERATE_FORM_MODEL_TEST({
    type: 'validation',
    form
  })
}

function handleValueUpdate (_KEY) {
  if (_vertical.value === 'OVHC') {
    const funcObj = getKey(_KEY, {
      visa_type: () => {
        formModel.value.visa_subtype = ''
      },

      visa_subtype: () => {
        // add/remove visa_code key in formModel & validation refs
        if (formModel.value.visa_subtype !== 'Other visa subclass') {
          formModel.value.visa_code = ''

          delete formModel.value.visa_code
          delete validation.value.visa_code
        } else {
          formModel.value.visa_code = _storeModel.value.visa_code ?? ''
          validation.value.visa_code = {
            isSuccess: true,
            errorMessage: ''
          }
        }
      }
    })

    if (funcObj) {
      funcObj()
    }
  }
}

function handleValidateForm (_KEY) {
  const validate = (question, tempValue) => {
    const map = getKey(question, {
      default: value => !_isEmpty(value),
      age: value => value ? validateAge(+value, [16, 120]) : false,
      annual_income: value => value ? validateAge(+value.replace(/[$,]/g, ''), [20000, 1000000]) : false,
      cover_amount: value => value ? validateAge(+value.replace(/[$,]/g, ''), [100000, 2000000]) : false
    })

    if (map) {
      return map?.(tempValue)
    }
  }

  if (_KEY) {
    const key = strToSnakeCase(_KEY)
    const currValue = formModel.value[key]
    const isValid = validate(key, currValue)?.valid ?? validate(key, currValue)

    validation.value[key] = {
      isSuccess: isValid,
      errorMessage: isValid
        ? ''
        : ERROR_MAP?.[key] ?? ''
    }

    return
  }

  for (const key in validation.value) {
    if (Object.hasOwnProperty.call(validation.value, key)) {
      const currValue = formModel.value?.[key]
      const isValid = validate(key, currValue)?.valid ?? validate(key, currValue)

      validation.value[key] = {
        isSuccess: isValid,
        errorMessage: isValid
          ? ''
          : ERROR_MAP?.[key] ?? ''
      }
    }
  }
}

async function handleSubmit () {
  await nextTick()

  await handleValidateForm()

  const hasError = (Object.values(validation.value)
    .map(item => item.isSuccess)
    .some(item => !item))
    
  if (hasError) {
    return
  }

  const model = formModel.value

  const map = getKey(_vertical.value, {
    [VERTICAL.HEALTH_INSURANCE]: async () => {
      const postcode = model?.postcode
      const postcodeText = `${postcode.suburb},${postcode.state},${postcode.postcode}`

      const payload = {
        step1: {
          life_stage: model?.life_stage,
          postcode
        }
      }

      healthInsuranceStore.form = JSON.parse(JSON.stringify(payload))
      healthInsuranceStore.guides.url = $route.path
      healthInsuranceStore.guides.initialPage = VERTICAL_DATA.value.redirect

      await nextTick()
      $router.push(`${VERTICAL_DATA.value.redirect}?life_stage=${model?.life_stage}&postcode=${postcodeText}`)
    },

    OVHC: async () => { 
      model.motivation = 'Unknown'

      const payload = {
        step1: {
          ...model
        }
      }

      const query = `?${Object.entries(model)
        .reduce((acc, curr, index) => {
          const lastItem = index === (Object.entries(model).length - 1)
          return acc += `${curr[0]}=${curr[1]}${!lastItem ? '&' : '' }`
        }, '')}`

      ovhcStore.form = JSON.parse(JSON.stringify(payload))
      ovhcStore.guides.url = $route.path
      ovhcStore.guides.initialPage = VERTICAL_DATA.value.redirect

      await nextTick()
      $router.push(`${VERTICAL_DATA.value.redirect}${query}`)
    },

    [VERTICAL.LIFE_INSURANCE]: async () => {
      const payload = {
        step1: {
          age: model?.age,
          annual_income: model?.cover_amount
        }
      }

      lifeInsuranceStore.form = JSON.parse(JSON.stringify(payload))
      lifeInsuranceStore.guides.url = $route.path
      lifeInsuranceStore.guides.initialPage = VERTICAL_DATA.value.redirect

      await nextTick()
      $router.push(`${VERTICAL_DATA.value.redirect}?age=${model?.age}&annual_income=${model?.cover_amount}`)
    },

    'Income Protection': async () => {
      const payload = {
        age: model?.age,
        annual_income: model?.annual_income
      }

      incomeProtectionStore.form = JSON.parse(JSON.stringify(payload))

      await nextTick()
      $router.push(`${VERTICAL_DATA.value.redirect}?age=${model?.age}&annual_income=${model?.annual_income}`)
    },

    [VERTICAL.HOME_LOANS]: async () => {
      const payload = {
        step1: {
          refinance: model?.refinance
        }
      }

      homeLoansStore.form = JSON.parse(JSON.stringify(payload))
      homeLoansStore.guides.url = $route.path
      homeLoansStore.guides.initialPage = VERTICAL_DATA.value.redirect

      await nextTick()
      $router.push(`${VERTICAL_DATA.value.redirect}?refinance=${model?.refinance}`)
    },

    [VERTICAL.ENERGY]: async () => {
      const payload = {
        step1: {
          fuel_type: model?.fuel_type
        }
      }

      energyStore.form = JSON.parse(JSON.stringify(payload))
      energyStore.guides.url = $route.path
      energyStore.guides.initialPage = VERTICAL_DATA.value.redirect

      await nextTick()
      $router.push(`${VERTICAL_DATA.value.redirect}?fuel_type=${model?.fuel_type}`)
    },

    [VERTICAL.CAR_INSURANCE]: async () => {
      const payload = {
        registration: {
          state: model?.state,
          type_of_cover: model?.type_of_cover
        }
      }

      carInsuranceStore.form = JSON.parse(JSON.stringify(payload))
      carInsuranceStore.guides.url = $route.path
      carInsuranceStore.guides.initialPage = VERTICAL_DATA.value.redirect

      await await nextTick()
      $router.push(`${VERTICAL_DATA.value.redirect}?state=${model?.state}&type_of_cover=${model?.type_of_cover}`)
    }
  })

  if (map) {
    await map?.()
  }
}
</script>
